import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { getAllSubjects } from "../helper/apicalls/subject";
import {
  getAllCoursePageContent,
  getCompletedCourseApi,
  getCoursePageContentById,
  updateCompletedCourseApi,
  getCourseByKey,
  getCompletionSummaryApi,
  getAllCourseInstitutionPageContent,
  getCourseInstitutionPageContentById,
  getOneCourseCompletedApi,
} from "../helper/apicalls/course";
import RenderPage from "../components/renderPage";
import PageSelection from "../components/pageSelection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines } from "@fortawesome/free-regular-svg-icons";
import "../styles/Subject.scss";
import { useNavigate } from "react-router-dom";
import SuccessPopup from "../components/common/SuccessPopup";
import ConversationalAnalysis from "../components/ConversationalAnalysis";
import CloseIcon from "@mui/icons-material/Close";
import {
  updateUserCoins,
  updateUserXP,
  checkIsAdmin,
} from "../helper/authUtils";
import edluminaLogo from "../assets/png/edluminaLogo.png";

const Subject = () => {
  const { coursekey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [subjects, setSubjects] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [selectRenderPage, setSelectRenderPage] = useState("subject");
  const [rightItems, setRightItems] = useState([]);
  const [contentData, setContentData] = useState(null);
  const [pageName, setPageName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectPageComponent, setSelectPageComponent] = useState("");
  const [isMarkAsComplete, setIsMarkAsComplete] = useState(false);
  const [parentId, setParentId] = useState("");
  const [renderParentId, setRenderParentId] = useState("");
  const [mcqCurrent, setMcqCurrent] = useState(0);
  const [mcqAnswers, setMcqAnswers] = useState({});
  const [completed, setCompleted] = useState();
  const [successPopup, setSuccessPopup] = useState(false);
  const [showAssistant, setShowAssistant] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [audioUrl, setAudioUrl] = useState();
  const [contentQuizData, setContentQuizData] = useState(null);
  const lastFetchedRef = useRef({ pageId: null, isCompleted: null });
  const [isPageLocked, setIsPageLocked] = useState(false);

  const isPageUnlocked = (item, completedOverride) => {
    if (isAdmin) return true;
    if (item.is_institution) return true;
    if (
      item.page_type === "active_recall" ||
      item.page_type === "active_recall_test" ||
      item.page_type === "active_recall_content" ||
      item.page_type === "materials" ||
      item.page_type === "material_item" ||
      item.page_type === "subject" ||
      item.header
    )
      return true;

    const flattenedStatuses = {};
    const traverse = (arr) => {
      if (!arr || !Array.isArray(arr)) return;
      arr.forEach((it) => {
        const pageId =
          typeof it.page === "object"
            ? it.page?._id || it.page?.id
            : it.page || it.active_recall || it.active_recall_id;
        if (pageId) {
          flattenedStatuses[pageId] = {
            completed: it.completed,
            teacher_completed: it.teacher_completed,
            is_locked: it.is_locked,
          };
        }
        if (it.child_pages) traverse(it.child_pages);
      });
    };
    const sourceCompleted = completedOverride || completed;
    
    const dataPart = sourceCompleted?.data;
    if (Array.isArray(dataPart)) {
      traverse(dataPart);
    } else if (dataPart && Array.isArray(dataPart.data)) {
      traverse(dataPart.data);
    }
    
    const activeRecallPart = sourceCompleted?.active_recall;
    if (Array.isArray(activeRecallPart)) {
      traverse(activeRecallPart);
    } else if (dataPart && Array.isArray(dataPart.active_recall)) {
      traverse(dataPart.active_recall);
    }

    const status = flattenedStatuses[item.id];

    if (status && status.is_locked !== undefined) {
      if (status.completed) return true;
      return !status.is_locked && status.teacher_completed !== false;
    }

    const firstItem = rightItems?.find((it) => !it.header);
    if (firstItem && item.id === firstItem.id) return true;

    return false;
  };

  useEffect(() => {
    if (!rightItems || rightItems.length === 0 || !selectPageComponent) return;
    const currentItem = findItemById(rightItems, selectPageComponent);
    if (!currentItem) return;
    const locked = !isPageUnlocked(currentItem);
    setIsPageLocked(locked);
  }, [completed, rightItems, selectPageComponent, isAdmin]);

  const refreshCompletionStatus = () => {
    if (coursekey) {
      getCompletedCourseApi("course", coursekey)
        .then((res) => {
          if (res) {
            setCompleted(res.data);
          }
        })
        .catch((err) => {
          console.error("Error refreshing completed status:", err);
        });
    }
  };

  const refreshContentQuizData = (pageId) => {
    if (!pageId) return;
    getOneCourseCompletedApi(pageId)
      .then((res) => {
        if (res) {
          setContentQuizData(res);
        }
      })
      .catch((err) => {
        console.error("Error fetching single course completed status:", err);
      });
  };

  useEffect(() => {
    setIsAdmin(checkIsAdmin());
  }, []);

  useEffect(() => {
    setMcqCurrent(0);
    setMcqAnswers({});
  }, [selectPageComponent]);

  const markInstitutionContent = (items) => {
    if (!items) return [];
    return items.map((item) => ({
      ...item,
      is_institution: true,
      child_pages: item.child_pages
        ? markInstitutionContent(item.child_pages)
        : undefined,
    }));
  };

  const findItemById = (itemsList, id) => {
    if (!itemsList || !id) return null;
    for (const item of itemsList) {
      if (String(item?.id) === String(id)) return item;
      if (Array.isArray(item?.child_pages) && item.child_pages.length > 0) {
        const found = findItemById(item.child_pages, id);
        if (found) return found;
      }
    }
    return null;
  };

  useEffect(() => {
    if (selectRenderPage === "content" && selectPageComponent) {
      const currentItem = findItemById(rightItems, selectPageComponent);
      const isInstitution = currentItem?.is_institution || false;
      if (!isInstitution) {
        const isCurrentlyCompleted = (() => {
          if (!completed) return false;

          const checkList = (list) => {
            if (!Array.isArray(list)) return false;
            for (const item of list) {
              const itemId =
                typeof item.page === "object"
                  ? item.page?._id || item.page?.id
                  : item.page;
              if (
                String(itemId) === String(selectPageComponent) &&
                item.completed
              ) {
                return true;
              }

              if (Array.isArray(item.child_pages)) {
                const childMatch = item.child_pages.find((child) => {
                  const childPageId =
                    typeof child.page === "object"
                      ? child.page?._id || child.page?.id
                      : child.page;
                  return (
                    String(childPageId) === String(selectPageComponent) &&
                    child.completed
                  );
                });
                if (childMatch) {
                  return true;
                }
              }
            }
            return false;
          };

          return (
            checkList(completed?.data) || checkList(completed?.active_recall)
          );
        })();

        // if (isCurrentlyCompleted || isMarkAsComplete) {
        //   const isCompleted = isCurrentlyCompleted || isMarkAsComplete;
        //   if (
        //     lastFetchedRef.current.pageId !== selectPageComponent ||
        //     lastFetchedRef.current.isCompleted !== isCompleted
        //   ) {
        //     lastFetchedRef.current = { pageId: selectPageComponent, isCompleted };
        if (
          !contentQuizData ||
          contentQuizData[0]?.course_page_content !== selectPageComponent
        ) {
          refreshContentQuizData(selectPageComponent);
        }
      }
      // }
    }
  }, [
    selectPageComponent,
    selectRenderPage,
    completed,
    rightItems,
    isMarkAsComplete,
    contentQuizData,
  ]);

  const fetchAllData = async () => {
    try {
      setIsLoading(true);
      if (coursekey) {
        const courseData = await getCourseByKey(coursekey);
        let currentParentId = "";
        if (courseData) {
          setParentId(courseData.id);
          setRenderParentId(courseData.id);
          currentParentId = courseData.id;
        }

        const [
          subjectsRes,
          completedRes,
          summaryRes,
          rightItemsRes,
          institutionItemsRes,
        ] = await Promise.all([
          getAllSubjects(coursekey),
          getCompletedCourseApi("course", coursekey),
          getCompletionSummaryApi("course", coursekey),
          getAllCoursePageContent(coursekey),
          getAllCourseInstitutionPageContent(coursekey).catch((err) => {
            console.error(
              "Error fetching course institution page content:",
              err,
            );
            return [];
          }),
        ]);

        if (subjectsRes && subjectsRes.length > 0) {
          setSubjects(subjectsRes);
          setActiveId(subjectsRes[0].id);
        }

        if (completedRes) {
          const isAdminUser = checkIsAdmin();
          setCompleted({
            ...completedRes?.data,
            next_data: isAdminUser
              ? completedRes?.data?.next_data
              : summaryRes?.data?.next_data || summaryRes?.data,
          });
        }

        let mergedRightItems =
          rightItemsRes && Array.isArray(rightItemsRes) ? rightItemsRes : [];
        if (
          institutionItemsRes &&
          Array.isArray(institutionItemsRes) &&
          institutionItemsRes.length > 0
        ) {
          const markedInstItems = markInstitutionContent(institutionItemsRes);
          mergedRightItems = [...mergedRightItems, ...markedInstItems];
        }

        if (mergedRightItems && mergedRightItems.length > 0) {
          const rightItemsList = [
            {
              id: "subject",
              name: "Course",
              page_type: "subject",
              sequence: 1,
              title: "Course",
            },
            ...mergedRightItems,
          ];
          setRightItems(rightItemsList);

          const itemToSelect = selectPageFromApiResponse(rightItemsList);

          if (itemToSelect) {
            setSelectPageComponent(itemToSelect.id);
            setSelectRenderPage(itemToSelect.page_type);
            setRenderParentId(currentParentId);
            const locked = !isPageUnlocked(itemToSelect, completedRes);
            setIsPageLocked(locked);
            if (
              itemToSelect.id !== "subject" &&
              itemToSelect.page_type !== "test"
            ) {
              fetchContentDetails(itemToSelect.id, itemToSelect.is_institution);
            }
          }
        } else {
          setSelectRenderPage("subject");
        }
      }
    } catch (error) {
      console.error("Failed to fetch initial data", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompletedClick = async () => {
    try {
      setIsLoading(true);
      if (coursekey) {
        const response = await updateCompletedCourseApi("course", {
          course_page_id: selectPageComponent,
          key: coursekey,
          completed: true,
        });
        if (response) {
          setCompleted((prevCompleted) => {
            const updatedCompleted = { ...prevCompleted };

            if (
              updatedCompleted?.data &&
              Array.isArray(updatedCompleted.data)
            ) {
              const pageExists = updatedCompleted.data.some(
                (item) => item.page === selectPageComponent,
              );

              if (!pageExists) {
                updatedCompleted.data.push({
                  page: selectPageComponent,
                  completed: true,
                });
              } else {
                updatedCompleted.data = updatedCompleted.data.map((item) =>
                  item.page === selectPageComponent
                    ? { ...item, completed: true }
                    : item,
                );
              }
            } else {
              updatedCompleted.data = [
                {
                  page: selectPageComponent,
                  completed: true,
                },
              ];
            }

            return updatedCompleted;
          });

          setIsMarkAsComplete(true);
          if (response.data?.page_completion_coins) {
            await updateUserCoins();
          }
          if (response.data?.page_completion_xp) {
            updateUserXP(response.data.page_completion_xp);
          }

          return response;
        }
      }
    } catch (error) {
      console.error("Failed to fetch subjects", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getFirstContentPage = (item) => {
    if (item?.child_pages && item.child_pages.length > 0) {
      return getFirstContentPage(item.child_pages[0]);
    }
    return item;
  };

  const selectPageFromApiResponse = (rightItemsRes) => {
    if (!rightItemsRes || rightItemsRes.length === 0) return null;

    let itemToSelect = null;

    const presentPageId = Cookies.get("present_pageid");
    if (presentPageId) {
      const findItemInResponse = (items, pageId) => {
        for (const item of items) {
          if (String(item.id) === String(pageId)) return item;
          if (item.child_pages && item.child_pages.length > 0) {
            const found = findItemInResponse(item.child_pages, pageId);
            if (found) return found;
          }
        }
        return null;
      };

      itemToSelect = findItemInResponse(rightItemsRes, presentPageId);
    }

    if (!itemToSelect) {
      itemToSelect = rightItemsRes[0];
    }

    return itemToSelect;
  };

  const fetchContentDetails = async (id, isInstitution = false) => {
    try {
      if (id !== "subject") {
        setIsLoading(true);

        const response = isInstitution
          ? await getCourseInstitutionPageContentById(id)
          : await getCoursePageContentById(id);
        if (response) {
          setContentData(response.content);
          setPageName(response.name);
          setAudioUrl(response?.tts_s3_url);
        }
      }
    } catch (error) {
      console.error("Failed to fetch content details", error);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (coursekey && !successPopup) {
      fetchAllData();
    }
  }, [successPopup, coursekey]);

  const questionsForSummary =
    selectRenderPage === "quiz"
      ? contentData
      : selectRenderPage === "truefalse"
        ? contentData?.questions || contentData
        : null;

  const mcqProps =
    (selectRenderPage === "quiz" || selectRenderPage === "truefalse") &&
    questionsForSummary
      ? {
          questions: questionsForSummary,
          current: mcqCurrent,
          answers: mcqAnswers,
          onNavigate: setMcqCurrent,
          pageType: selectRenderPage,
          pageName: pageName,
        }
      : null;

  const findItemInList = (items, targetId) => {
    if (!items) return null;
    for (const item of items) {
      if (String(item.id) === String(targetId)) return item;
      if (item.child_pages && item.child_pages.length > 0) {
        const found = findItemInList(item.child_pages, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const activeItem = findItemInList(rightItems, selectPageComponent);
  const isActiveInstitution = activeItem?.is_institution || false;

  const [showRightPanel, setShowRightPanel] = useState(false);

  return (
    <div className="subject-layout">
      <div className="subject-layout__left">
        {rightItems?.length > 0 && (
          <div
            className="mobile-toggle-btn"
            onClick={() => setShowRightPanel(!showRightPanel)}
          >
            <FontAwesomeIcon icon={faFileLines} />
          </div>
        )}
        <div className="subject-scroll-area custom-scrollbar">
          <RenderPage
            activeComponent={selectRenderPage}
            subjects={subjects}
            activeId={selectPageComponent}
            onSelectLeft={(item) => {
              setActiveId(item.id);
              navigate(`/courses/${coursekey}/${item.key}`);
            }}
            contentData={contentData}
            isLoading={isLoading}
            mcqCurrent={mcqCurrent}
            setMcqCurrent={setMcqCurrent}
            mcqAnswers={mcqAnswers}
            setMcqAnswers={setMcqAnswers}
            pageName={pageName}
            pageKey={coursekey}
            pageTitle={"course"}
            completed={completed}
            rightActiveId={selectPageComponent}
            parent={renderParentId}
            handleCompletedClick={handleCompletedClick}
            isMarkAsComplete={isMarkAsComplete}
            setIsMarkAsComplete={setIsMarkAsComplete}
            audiourl={audioUrl}
            isInstitution={isActiveInstitution}
            contentQuizData={contentQuizData}
            refreshContentQuizData={refreshContentQuizData}
            onQuizSubmitSuccess={refreshCompletionStatus}
            isPageLocked={isPageLocked}
          />
        </div>

        {selectRenderPage === "content" && !isAdmin && (
          <>
            <div
              className="assistant-trigger"
              onClick={() => setShowAssistant(!showAssistant)}
            >
              <img
                src={edluminaLogo}
                height={45}
                className="assistant-image"
                alt="Edlumina logo"
              />
            </div>

            {showAssistant && (
              <div className="assistant-popup">
                <div className="assistant-header">
                  <h3>Ainstein</h3>
                  <button
                    className="close-btn"
                    onClick={() => setShowAssistant(false)}
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
                <div className="assistant-body">
                  <ConversationalAnalysis
                    question="Welcome to Ainstein what is your doubt?"
                    contentConvo={true}
                    apiQueryParams={{
                      id: selectPageComponent || activeId,
                      key: coursekey,
                      title: "course",
                    }}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {showRightPanel && (
        <div
          className="mobile-backdrop"
          onClick={() => setShowRightPanel(false)}
        />
      )}
      <div
        className={`subject-layout__right ${showRightPanel ? "visible" : ""}`}
      >
        <PageSelection
          title="Topics"
          items={rightItems}
          activeId={selectPageComponent}
          level="course"
          parentKey={coursekey}
          pageTitle="course"
          onSelect={(data) => {
            setContentData(null);

            const parentPage = rightItems.find((item) =>
              item.child_pages?.some(
                (child) => String(child.id) === String(data.id),
              ),
            );

            const targetItem = getFirstContentPage(data);
            setSelectPageComponent(targetItem?.id);
            setSelectRenderPage(targetItem?.page_type);
            const locked = !isPageUnlocked(targetItem);
            setIsPageLocked(locked);

            let effectiveParent = parentId;
            if (parentPage) {
              effectiveParent = parentPage.id;
            } else if (data.child_pages && data.child_pages.length > 0) {
              effectiveParent = data.id;
            }
            setRenderParentId(effectiveParent);

            if (targetItem?.id) {
              fetchContentDetails(targetItem.id, targetItem.is_institution);
              Cookies.set("present_pageid", targetItem.id, { expires: 7 });
            }

            // Set contentTab=normal when content page is selected
            if (targetItem?.page_type === "content") {
              const searchParams = new URLSearchParams(location.search);
              searchParams.set("contentTab", "normal");
              navigate(`${location.pathname}?${searchParams.toString()}`, {
                replace: true,
              });
            }

            if (window.innerWidth <= 1024) {
              setShowRightPanel(false);
            }
          }}
          mcqProps={mcqProps}
          completed={completed}
          isAdmin={isAdmin}
          isPageLocked={isPageLocked}
        />
      </div>
      <SuccessPopup
        open={successPopup}
        onClose={() => {
          setSuccessPopup(false);
        }}
        message="Marked as Completed Successfully!"
      />
    </div>
  );
};

export default Subject;
